import { Formik } from "formik";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import NumberFormat from "react-number-format";

import { Col, Input, Row, Spin, notification } from "antd";

import { Creators as WagonActions } from "../../../store/ducks/wagon";
import { save, update, findById } from "../../../services/wagonService";

import ButtonStandard from "../../utils/button";
import { Container, Footer } from "./styles";

const INITIAL_FORM_STATE = {
  id: null,
  name: null,
  integrationCode: null,
  capacityKg: null,
  observations: null,
};

const validationSchema = Yup.object({
  name: Yup.string().trim().required(),
  capacityKg: Yup.number().moreThan(0).required(),
});

const DrawerWagon = () => {
  const formRef = useRef(null);
  const [loadRequest, setLoadRequest] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { wagonId, isDrawerVisible } = useSelector((state) => state.wagon);

  const dispatch = useDispatch();

  function handleCloseDrawer() {
    if (formRef.current) {
      formRef.current.resetForm(INITIAL_FORM_STATE);
    }
    dispatch(WagonActions.showOrHideDrawer());
  }

  function resolveErrorMessage(error, isUpdate) {
    const backendMsg = (
      error?.response?.data?.results?.message ||
      error?.response?.data?.message ||
      ""
    ).toLowerCase();

    if (backendMsg.includes("integration code")) {
      return translation.wagon.messages.integrationCodeAlreadyExists;
    }
    if (backendMsg.includes("name")) {
      return translation.wagon.messages.nameAlreadyExists;
    }
    return isUpdate
      ? translation.wagon.messages.updateError
      : translation.wagon.messages.saveError;
  }

  async function handleSubmitForm(values) {
    setLoadRequest(true);
    try {
      if (wagonId !== null) {
        await update({ groupId, farmId, id: wagonId, body: values });
        notification.success({
          message: translation.wagon.messages.updateSuccess,
        });
      } else {
        await save({ groupId, farmId, body: values });
        notification.success({
          message: translation.wagon.messages.saveSuccess,
        });
      }
      dispatch(WagonActions.index(groupId, farmId));
      setLoadRequest(false);
      handleCloseDrawer();
    } catch (error) {
      notification.error({
        message: resolveErrorMessage(error, wagonId !== null),
      });
      setLoadRequest(false);
    }
  }

  async function handleDrawerVisible(visible) {
    if (!visible) return;

    if (wagonId !== null) {
      setLoadRequest(true);
      try {
        const {
          data: { results: wagonData },
        } = await findById({ groupId, farmId, id: wagonId });
        if (wagonData && formRef.current) {
          formRef.current.resetForm({
            id: wagonData.id,
            name: wagonData.name,
            integrationCode: wagonData.integrationCode,
            capacityKg: wagonData.capacityKg,
            observations: wagonData.observations,
          });
        }
        setLoadRequest(false);
      } catch (error) {
        setLoadRequest(false);
      }
    } else if (formRef.current) {
      formRef.current.resetForm(INITIAL_FORM_STATE);
    }
  }

  return (
    <Container
      title={
        wagonId === null
          ? translation.wagon.drawer.titleNew
          : translation.wagon.drawer.titleEdit
      }
      width={700}
      onClose={handleCloseDrawer}
      maskClosable={false}
      visible={isDrawerVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={loadRequest}>
        <Formik
          ref={formRef}
          enableReinitialize
          initialValues={INITIAL_FORM_STATE}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="drawerForm">
                {wagonId === null && (
                  <Row type="flex">
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <span className="subTitle">
                        {translation.wagon.drawer.subTitle}
                      </span>
                    </Col>
                  </Row>
                )}

                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}

                {/* Name */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.name && submitCount > 0 ? "error" : ""}
                  >
                    {translation.wagon.drawer.form.name}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="name"
                    value={values.name || ""}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    maxLength={100}
                    onChange={(e) => setFieldValue("name", e.target.value)}
                  />
                </Row>

                {/* Integration Code */}
                <Row type="flex" className="rowLabel">
                  <label>{translation.wagon.drawer.form.integrationCode}</label>
                </Row>
                <Row type="flex">
                  <Input
                    name="integrationCode"
                    value={values.integrationCode || ""}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) =>
                      setFieldValue("integrationCode", e.target.value)
                    }
                  />
                </Row>

                {/* Capacity Kg */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.capacityKg && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.wagon.drawer.form.capacityKg}*
                  </label>
                </Row>
                <Row type="flex">
                  <NumberFormat
                    customInput={Input}
                    name="capacityKg"
                    value={values.capacityKg == null ? "" : values.capacityKg}
                    placeholder={translation.defaultPlaceholder}
                    decimalScale={2}
                    fixedDecimalScale
                    decimalSeparator=","
                    thousandSeparator="."
                    allowNegative={false}
                    isNumericString
                    onValueChange={({ floatValue }) =>
                      setFieldValue("capacityKg", floatValue ?? null)
                    }
                  />
                </Row>

                {/* Observations */}
                <Row type="flex" className="rowLabel">
                  <label>{translation.wagon.drawer.form.observations}</label>
                </Row>
                <Row type="flex">
                  <Input.TextArea
                    name="observations"
                    value={values.observations || ""}
                    rows={3}
                    placeholder={translation.defaultPlaceholder}
                    onChange={(e) =>
                      setFieldValue("observations", e.target.value)
                    }
                  />
                </Row>
              </div>

              <Footer>
                <Row type="flex">
                  <Col span={24} className="buttonsDiv">
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={handleCloseDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default DrawerWagon;
